import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import Header from '../components/Header';
import MobileActionBar from '../components/MobileActionBar';
import {
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Building2,
  Paintbrush,
  Hammer,
  Droplets,
  Layers,
  Home as HomeIcon,
  Key,
  Sparkles
} from 'lucide-react';
import { companyInfo } from '../data/mock';
const companyPhone = '07300 405045';
const RefurbishmentServices = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    property_type: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(false);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          properties_managed: formData.property_type,
          job_description: `[Refurbishment Enquiry] ${formData.message}`,
          trade_type: 'general',
          source_website: 'expert-propertymaintenance.co.uk',
          source_page: '/property-refurbishment'
        }),
      });
      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', property_type: '', message: '' });
      } else {
        setSubmitError(true);
      }
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  const refurbServices = [
    {
      icon: Paintbrush,
      title: 'Painting & Decorating',
      description: 'Complete interior and exterior painting. Wall preparation, filling, sanding and a professional finish throughout.'
    },
    {
      icon: Hammer,
      title: 'Carpentry & Joinery',
      description: 'New doors, skirting boards, architraves, built-in wardrobes, shelving and all woodwork repairs or replacements.'
    },
    {
      icon: Layers,
      title: 'Plastering',
      description: 'Patch repairs, full re-plasters, skimming and wall preparation. Creating smooth, ready-to-paint surfaces.'
    },
    {
      icon: HomeIcon,
      title: 'Flooring',
      description: 'Laminate, engineered wood, vinyl flooring installation and repairs. Floor preparation and levelling.'
    },
    {
      icon: Droplets,
      title: 'Kitchens',
      description: 'Kitchen unit repairs, worktop replacements, tiling, plumbing connections and full kitchen refreshes.'
    },
    {
      icon: Sparkles,
      title: 'Bathrooms',
      description: 'Bathroom updates, re-sealing, tiling repairs, fixture replacements and full bathroom improvements.'
    }
  ];
  const projectTypes = [
    {
      title: 'Void Property Turnaround',
      description: 'Complete property refresh between tenancies. Repairs, redecoration, flooring and preparation for new tenants.',
      features: ['Full redecoration', 'Repair works', 'Deep clean preparation', 'New flooring if required']
    },
    {
      title: 'Home Refresh & Renovation',
      description: 'Transform your home with a coordinated refurbishment. One contractor managing all the trades.',
      features: ['Kitchen & bathroom updates', 'Room-by-room decoration', 'Carpentry improvements', 'General repairs']
    },
    {
      title: 'Buy-to-Let Renovation',
      description: 'Full property renovation to maximise rental value and attract quality tenants.',
      features: ['Kitchen refresh', 'Bathroom update', 'Throughout decoration', 'Carpentry improvements']
    },
    {
      title: 'Tired Property Refresh',
      description: 'Bring dated or worn properties back to life with targeted improvements.',
      features: ['Painting & decorating', 'Floor repairs/replacement', 'General repairs', 'Fixture updates']
    }
  ];
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Property Refurbishment | Landlords & Homeowners | Expert Property Maintenance</title>
        <meta name="description" content="Property refurbishment for landlords, homeowners and investors in Manchester. Renovations, decorating, carpentry, kitchens, bathrooms. One contractor for multiple trades. Call 07300 405045." />
        <link rel="canonical" href="https://expert-propertymaintenance.co.uk/property-refurbishment" />
      </Helmet>
      <Header variant="default" companyPhone={companyPhone} />
      {/* Hero */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-20 bg-gradient-to-b from-brand-navy to-brand-navy/90">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Building2 className="w-4 h-4" />
              Property Refurbishment
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Property Refurbishment for{' '}
              <span className="text-brand-yellow">Landlords, Homeowners & Investors</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Whether you are preparing a property for new tenants, refreshing your home, 
              or completing a full renovation — Expert Property Maintenance handles refurbishment 
              projects across Greater Manchester.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold" asChild>
                <a href="#contact">
                  Discuss Your Project
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-navy" asChild>
                <a href={`tel:${companyPhone}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  {companyPhone}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* What We Cover */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-brand-navy mb-4">
              Refurbishment Services
            </h2>
            <p className="text-lg text-brand-slate max-w-2xl mx-auto">
              Multi-trade capability for complete property refurbishments — decorating, carpentry, 
              plastering, flooring, kitchens and bathrooms.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {refurbServices.map((service) => (
              <Card key={service.title} className="border-slate-200 hover:border-brand-yellow hover:shadow-lg transition-all group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-brand-grey rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-yellow transition-colors">
                    <service.icon className="w-6 h-6 text-brand-yellow group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-brand-navy mb-2">{service.title}</h3>
                  <p className="text-brand-slate text-sm">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* Project Types */}
      <section className="py-16 bg-brand-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-brand-navy mb-4">
              Typical Refurbishment Projects
            </h2>
            <p className="text-lg text-brand-slate max-w-2xl mx-auto">
              We work with landlords, investors and letting agents on properties of all sizes.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {projectTypes.map((project) => (
              <Card key={project.title} className="border-slate-200 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-brand-navy mb-3">{project.title}</h3>
                  <p className="text-brand-slate mb-4">{project.description}</p>
                  <ul className="space-y-2">
                    {project.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-sm text-brand-slate">
                        <CheckCircle2 className="w-4 h-4 text-brand-yellow flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      {/* What We Include */}
      <section className="py-16 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                What Refurbishment Can Include
              </h2>
              <p className="text-slate-300 mb-8">
                Every property is different. We tailor refurbishment projects to your budget and requirements.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  'Rental property refurbishments',
                  'Properties between tenancies',
                  'Tired or dated properties',
                  'Damaged rental properties',
                  'Decorating throughout',
                  'New flooring',
                  'Carpentry & joinery',
                  'Kitchen updates',
                  'Bathroom improvements',
                  'Plaster repairs',
                  'General repairs',
                  'Preparation for reletting'
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-800 rounded-2xl p-8">
              <h3 className="text-xl font-bold mb-6">Why Choose Us</h3>
              <div className="space-y-4">
                <div className="p-4 bg-slate-700/50 rounded-xl">
                  <h4 className="font-semibold mb-1">Multi-Trade Capability</h4>
                  <p className="text-sm text-slate-400">One contractor for carpentry, painting, plastering, plumbing and more.</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-xl">
                  <h4 className="font-semibold mb-1">Landlord Focused</h4>
                  <p className="text-sm text-slate-400">We understand rental properties and work efficiently to minimise void periods.</p>
                </div>
                <div className="p-4 bg-slate-700/50 rounded-xl">
                  <h4 className="font-semibold mb-1">Fully Insured</h4>
                  <p className="text-sm text-slate-400">£5 million public liability and 12-month workmanship guarantee.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Contact Form */}
      <section id="contact" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-brand-navy mb-6">Discuss Your Refurbishment Project</h2>
              <p className="text-lg text-brand-slate mb-8">
                Tell us about your property and what you want to achieve. 
                We will get back to you within 24 hours with initial thoughts and next steps.
              </p>
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-brand-slate/80 text-sm">Call us</div>
                    <a href={`tel:${companyPhone}`} className="text-brand-navy font-bold text-lg hover:text-brand-yellow">
                      {companyPhone}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-brand-slate/80 text-sm">Email us</div>
                    <a href={`mailto:${companyInfo.email}`} className="text-brand-navy font-bold hover:text-brand-yellow">
                      {companyInfo.email}
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-brand-yellow rounded-lg flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-brand-navy" />
                  </div>
                  <div>
                    <div className="text-brand-slate/80 text-sm">Service area</div>
                    <div className="text-brand-navy font-bold">Greater Manchester & Cheshire</div>
                  </div>
                </div>
              </div>
            </div>
            <Card className="bg-brand-grey border-slate-200">
              <CardContent className="p-6 sm:p-8">
                {isSubmitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-brand-navy mb-2">Enquiry Received</h3>
                    <p className="text-brand-slate">We will be in touch within 24 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <h3 className="text-xl font-bold text-brand-navy mb-4">Request a Refurbishment Quote</h3>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Your Name *</label>
                      <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" className="h-12 bg-white" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Email *</label>
                        <Input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@email.com" className="h-12 bg-white" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-brand-slate mb-1.5">Phone *</label>
                        <Input type="tel" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="07xxx xxxxxx" className="h-12 bg-white" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Property Type / Location</label>
                      <Input value={formData.property_type} onChange={(e) => setFormData({...formData, property_type: e.target.value})} placeholder="e.g., 2-bed flat in Stockport" className="h-12 bg-white" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-brand-slate mb-1.5">Tell us about your project *</label>
                      <Textarea required value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="What refurbishment work do you need?" rows={4} className="bg-white" />
                    </div>
                    <Button type="submit" className="w-full bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-bold h-12 text-lg" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Enquiry'}
                    </Button>
                  </form>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© {new Date().getFullYear()} Expert Property Maintenance - Property Refurbishment Manchester</p>
            <div className="flex gap-6">
              <a href="/" className="hover:text-brand-yellow">Home</a>
              <a href="/landlord-services" className="hover:text-brand-yellow">Landlord Services</a>
              <a href="/end-of-tenancy" className="hover:text-brand-yellow">End of Tenancy</a>
              <a href="/#contact" className="hover:text-brand-yellow">Contact</a>
            </div>
          </div>
        </div>
      </footer>
      <MobileActionBar phone={companyPhone} />
    </div>
  );
};
export default RefurbishmentServices;
